import { useState } from 'react';
import { X, Eye, EyeOff, CloudUpload } from 'lucide-react';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userData: any) => void;
}

interface UserFormData {
  username: string;
  governorate: string;
  name: string;
  password: string;
  shopName: string;
  address: string;
  email: string;
  passwordConfirmation: string;
  role: string;
  city: string;
  addressOptional: string;
  phone: string;
  profilePicture: File | null;
}

export default function CreateUserModal({ isOpen, onClose, onSubmit }: CreateUserModalProps) {
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    governorate: '',
    name: '',
    password: '1224893',
    shopName: '',
    address: '',
    email: '',
    passwordConfirmation: '********',
    role: '',
    city: '',
    addressOptional: '',
    phone: '',
    profilePicture: null
  });

  const [showPassword, setShowPassword] = useState(true);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: keyof UserFormData, value: string | File) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleInputChange('profilePicture', file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onSubmit(formData);
      onClose();
      // Reset form
      setFormData({
        username: '',
        governorate: '',
        name: '',
        password: '1224893',
        shopName: '',
        address: '',
        email: '',
        passwordConfirmation: '********',
        role: '',
        city: '',
        addressOptional: '',
        phone: '',
        profilePicture: null
      });
    } catch (error) {
      console.error('Error creating user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className={`fixed top-0 bg-white shadow-2xl transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          width: '980px',
          height: '100vh',
          right: '0px',
          top: '0px'
        }}
      >
          {/* Header */}
          <div className="bg-secondary-500 text-white p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-[40px] leading-none font-medium">Create User</h2>
                <p className="text-[24px] text-white/80 mt-2">Fill details and create user</p>
              </div>
              <button 
                onClick={onClose}
                className="bg-[#426198] px-2 py-2 rounded-full text-white hover:text-gray-300 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Form */}
          <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100vh - 120px)' }}>
            <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-3 gap-6">
              {/* Column 1 */}
              <div className="space-y-4">
                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    required
                    className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  />
                </div>

                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    Governorate
                  </label>
                  <input
                    type="text"
                    value={formData.governorate}
                    onChange={(e) => handleInputChange('governorate', e.target.value)}
                    className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  />
                </div>

                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  />
                </div>

                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      required
                      className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 pr-10 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? (
                        <Eye className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Column 2 */}
              <div className="space-y-4">
                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    Shop Name
                  </label>
                  <input
                    type="text"
                    value={formData.shopName}
                    onChange={(e) => handleInputChange('shopName', e.target.value)}
                    className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  />
                </div>

                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  />
                </div>

                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                    className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  />
                </div>

                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    Password Confirmation <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswordConfirmation ? 'text' : 'password'}
                      value={formData.passwordConfirmation}
                      onChange={(e) => handleInputChange('passwordConfirmation', e.target.value)}
                      required
                      className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 pr-10 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPasswordConfirmation ? (
                        <Eye className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  >
                    <option value="">Select</option>
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
              </div>

              {/* Column 3 */}
              <div className="space-y-4">
                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  />
                </div>

                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    Address (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.addressOptional}
                    onChange={(e) => handleInputChange('addressOptional', e.target.value)}
                    className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  />
                </div>

                <div>
                  <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  />
                </div>
              </div>
            </div>

            {/* Profile Picture Upload */}
            <div className="mt-8">
              <label className="block text-[16px] font-semibold text-[#111318] mb-3">
                Upload Profile Picture
              </label>
              <div className="border border-dashed border-yellow-400 bg-[#FFF7DA] rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="profile-picture-upload"
                />
                <label htmlFor="profile-picture-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center">
                    <CloudUpload className="h-12 w-12 text-yellow-500 mb-3" />
                    <p className="text-sm text-[#6A7698]">
                      {formData.profilePicture ? formData.profilePicture.name : 'Click to upload profile picture'}
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-lg bg-[#F6C600] py-3 px-6 text-[18px] font-medium text-black transition-colors hover:bg-[#e1b500] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
